import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation('footer');

  return (
    <footer className="bg-gray-50 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-2xl">🛡️</span>
              <span className="font-bold text-xl text-gray-900">{t('brand')}</span>
            </div>
            {/* Keep description static for now or add to locales later */}
            <p className="text-gray-600 max-w-md">
              Helping Toronto residents dispute parking tickets with AI-powered appeal letters. 
              Save time and money with professional legal assistance.
            </p>
            <div className="mt-6">
              <p className="text-sm text-gray-500">
                Success rate: <span className="font-semibold text-primary-600">78%</span> of appeals approved
              </p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
              {t('quick.title')}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/how-it-works" className="text-gray-600 hover:text-primary-600 transition-colors duration-200">
                  {t('quick.how')}
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-gray-600 hover:text-primary-600 transition-colors duration-200">
                  {t('quick.pricing')}
                </Link>
              </li>
              <li>
                <Link to="/success-stories" className="text-gray-600 hover:text-primary-600 transition-colors duration-200">
                  {t('quick.stories')}
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-gray-600 hover:text-primary-600 transition-colors duration-200">
                  {t('quick.faq')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
              {t('support.title')}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/contact" className="text-gray-600 hover:text-primary-600 transition-colors duration-200">
                  {t('support.contact')}
                </Link>
              </li>
              <li>
                <Link to="/help" className="text-gray-600 hover:text-primary-600 transition-colors duration-200">
                  {t('support.help')}
                </Link>
              </li>
              <li>
                <a 
                  href="mailto:support@disputebottoronto.com" 
                  className="text-gray-600 hover:text-primary-600 transition-colors duration-200"
                >
                  {t('support.email')}
                </a>
              </li>
              <li>
                <a 
                  href="tel:+1-416-555-0123" 
                  className="text-gray-600 hover:text-primary-600 transition-colors duration-200"
                >
                  {t('support.phone')}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex flex-wrap items-center space-x-6 mb-4 md:mb-0">
              <Link 
                to="/privacy-policy" 
                className="text-sm text-gray-500 hover:text-primary-600 transition-colors duration-200"
              >
                {t('links.privacy')}
              </Link>
              <Link 
                to="/terms-of-service" 
                className="text-sm text-gray-500 hover:text-primary-600 transition-colors duration-200"
              >
                {t('links.terms')}
              </Link>
              <Link 
                to="/disclaimer" 
                className="text-sm text-gray-500 hover:text-primary-600 transition-colors duration-200"
              >
                {t('links.disclaimer')}
              </Link>
            </div>

            <div className="text-sm text-gray-500">
              {t('copyright', { year: currentYear })}
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-sm text-yellow-800">
              <span className="font-semibold">{t('legal.title')}</span> {t('legal.text')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
